import type { PlanSummary } from "@edu/shared";
import { reasonText, showsNoKeyNote, type AnswerableView } from "./answerable.ts";

/**
 * Ветка «Из заготовки»: выбор заготовки и состав из её отвечаемых заданий (5.5
 * docs/homework-phase2-design.md).
 *
 * ⚠️ Переключатель источника живёт не здесь, а в `ExerciseSource`: обе ветки
 * равноправны, и та, что рисует переключатель, однажды начала бы решать за
 * вторую.
 *
 * Задания заготовки у всех учеников раздачи ОДИНАКОВЫЕ: заготовка
 * компилируется из одного источника, а не бросает зерно на каждого. Этим ветка
 * отличается от «По темам», и предпросмотр обязан говорить об этом вслух.
 *
 * ⚠️ **Задание без верного ответа стоит в списке, но не в счёте** (правка
 * 03.09.2026). Сервер такое задание в домашку молча не берёт, и до этой правки
 * человек видел восемь галочек, предпросмотр «Задания (8)» и получал пять работ
 * по пять заданий. Отказ на его месте был бы хуже: у пропусков и свободного
 * ввода эталона нет, пока условие ещё печатают, и отказ приходил бы не тому,
 * кто ошибся. Поэтому правда говорится ДО нажатия, а не после: пометка в
 * строке, флажок недоступен, число сходится.
 */
export function PlanSource(props: {
  plans: PlanSummary[];
  planId: string;
  planLoaded: boolean;
  answerable: AnswerableView[];
  excluded: ReadonlySet<string>;
  onPlanId: (id: string) => void;
  onToggle: (elementId: string) => void;
}) {
  return (
    <>
      <p>
        <select
          className="fld"
          value={props.planId}
          aria-label="Заготовка"
          onChange={(e) => props.onPlanId(e.target.value)}
        >
          {/*
          Ни длинного тире, ни среднего: запрет глобальный и на интерфейс
          распространяется наравне с текстами. Слова те же и с той же заглавной,
          что у соседних пустых пунктов («Выберите группу», «Выберите ученика»)
        */}
        <option value="">Выберите заготовку</option>
          {props.plans.map((p) => (
            <option key={p.id} value={p.id}>
              {p.title}
            </option>
          ))}
        </select>
      </p>

      {props.planLoaded && props.answerable.length === 0 && (
        <p className="muted">В этой заготовке нет заданий с проверкой: в домашку нечего взять.</p>
      )}

      {props.answerable.length > 0 && (
        <ul className="hwform__list">
          {props.answerable.map((ex) => (
            <li key={ex.id}>
              <label className="form__check">
                {/*
                  Флажок недоступен, а не просто снят: нажатие на нём ничего бы
                  не изменило, а доступный флажок, который не считается, это то
                  же враньё в другом месте
                */}
                <input
                  type="checkbox"
                  checked={ex.ready && !props.excluded.has(ex.id)}
                  disabled={!ex.ready}
                  onChange={() => props.onToggle(ex.id)}
                />
                <span className={ex.ready ? undefined : "muted"}>{ex.label}</span>
                {ex.reason !== null && <span className="muted"> {reasonText(ex.reason)}</span>}
              </label>
            </li>
          ))}
        </ul>
      )}

      {/*
        Строка появляется, только когда такие задания есть: постоянный текст про
        редкий случай это плата за него каждым открытием формы
      */}
      {showsNoKeyNote(props.answerable) && (
        <p className="muted">
          Задания без правильного ответа в домашку не попадут. Поставить его можно в
          заготовке.
        </p>
      )}

      {props.planLoaded && (
        <p className="muted">
          У ученика задание будет без слайда: снимите флажок с того, что без слайда
          непонятно.
        </p>
      )}
    </>
  );
}
